<script setup lang="ts">
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import { ArrowLeft, Columns3, MousePointer2 } from 'lucide-vue-next'
import { ShareAndroid } from '@iconoir/vue'
import EmailEditorBlockHeader from './EmailEditorBlockHeader.vue'
import ColorPickerWithPresets from './ColorPickerWithPresets.vue'
import GalleryPresetIcon from './blocks/GalleryPresetIcon.vue'

import type { Component } from 'vue'
import type { BlockType, EmailBlock, GalleryColumns, GalleryItem } from '../types'
import type { EmailStyles } from '../utils/apiTransformers'
import type { Icons } from '@/components/base/BaseIcon.vue'

const { t } = useI18n()

const fontOptions = computed(() => [
  {
    label: t('pages.email.emailTemplateCreatePage.panel.fontSystemRecommended'),
    description: 'San Francisco (Apple), Segoe UI (Windows), Roboto (Android)',
    value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  { label: 'Inter', description: 'Inter, sans-serif', value: 'Inter, sans-serif' },
  { label: 'Arial', description: 'Arial, sans-serif', value: 'Arial, sans-serif' },
  { label: 'Helvetica', description: 'Helvetica, Arial, sans-serif', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Georgia', description: 'Georgia, serif', value: 'Georgia, serif' },
  { label: 'Times New Roman', description: 'Times New Roman, Times, serif', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', description: 'Verdana, Geneva, sans-serif', value: 'Verdana, Geneva, sans-serif' }
])

const fontComboboxOptions = computed(() => fontOptions.value.map((o) => ({ value: o.value, label: o.label })))

const emailStyles = defineModel<EmailStyles>('emailStyles', { required: true })

const props = defineProps<{
  colorPresets: { color: string; label: string }[]
  selectedBlock: EmailBlock | null
  selectedCellIndex?: number | null
  selectedCell?: GalleryItem | null
  headerSelected?: boolean
}>()

const emit = defineEmits<{
  (e: 'deselect'): void
}>()

// Text & footer are formatted from the horizontal toolbar above the canvas, but
// their settings panel still hosts the per-block background color picker.
const panelView = computed<'add' | 'settings'>(() => {
  if (props.headerSelected) return 'settings'
  if (!props.selectedBlock) return 'add'
  return 'settings'
})

const blockTypeLabels: Record<BlockType, string> = {
  text: 'componentText',
  image: 'componentImage',
  button: 'componentButton',
  spacer: 'componentSpacer',
  separator: 'componentSeparator',
  gallery: 'componentGalleryGrid2Cols',
  footer: 'blockFooter',
  social: 'componentSocial'
}

const blockTypeIcons: Partial<Record<BlockType, Icons>> = {
  text: 'IconoirText',
  image: 'HeroPhotoIconSolid',
  button: 'IconoirCursorPointer',
  spacer: 'HeroArrowsUpDownIconSolid',
  separator: 'HeroMinusIconSolid',
  gallery: 'HeroSquare2StackIconSolid',
  footer: 'IconoirLayoutLeft',
  social: 'IconoirGlobe'
}

const selectedBlockLabel = computed(() => {
  if (props.headerSelected) return t('pages.email.emailTemplateCreatePage.blockHeader')
  if (props.selectedCell) {
    return props.selectedCell.type === 'text'
      ? t('pages.email.emailTemplateCreatePage.blockText')
      : t('pages.email.emailTemplateCreatePage.blockImage')
  }
  if (!props.selectedBlock) return ''
  const key = blockTypeLabels[props.selectedBlock.type]
  if (props.selectedBlock.type === 'gallery') {
    return t('pages.email.emailTemplateCreatePage.blockGallery')
  }
  if (props.selectedBlock.type === 'footer') {
    return t(`pages.email.emailTemplateCreatePage.${key}`)
  }
  return t(`pages.email.emailTemplateCreatePage.panel.${key}`)
})

const selectedBlockIcon = computed<Icons | undefined>(() => {
  if (props.headerSelected) return 'HeroPhotoIconSolid'
  if (props.selectedCell) {
    return blockTypeIcons[props.selectedCell.type === 'text' ? 'text' : 'image']
  }
  return props.selectedBlock ? blockTypeIcons[props.selectedBlock.type] : undefined
})

interface DraggableComponent {
  key: string
  type: BlockType
  label: string
  icon?: Icons
  iconComponent?: Component
  columns?: GalleryColumns
}

const baseComponents = computed<DraggableComponent[]>(() => [
  {
    key: 'text',
    type: 'text',
    label: t('pages.email.emailTemplateCreatePage.panel.componentText'),
    icon: 'IconoirText'
  },
  {
    key: 'image',
    type: 'image',
    label: t('pages.email.emailTemplateCreatePage.panel.componentImage'),
    icon: 'HeroPhotoIconSolid'
  },
  {
    key: 'button',
    type: 'button',
    label: t('pages.email.emailTemplateCreatePage.panel.componentButton'),
    icon: 'IconoirCursorPointer'
  },
  {
    key: 'spacer',
    type: 'spacer',
    label: t('pages.email.emailTemplateCreatePage.panel.componentSpacer'),
    icon: 'HeroArrowsUpDownIconSolid'
  },
  {
    key: 'separator',
    type: 'separator',
    label: t('pages.email.emailTemplateCreatePage.panel.componentSeparator'),
    icon: 'HeroMinusIconSolid'
  },
  {
    key: 'social',
    type: 'social',
    label: t('pages.email.emailTemplateCreatePage.panel.componentSocial'),
    iconComponent: ShareAndroid
  }
])

const layoutComponents = computed<DraggableComponent[]>(() => [
  {
    key: 'gallery-1col',
    type: 'gallery',
    columns: 1,
    label: t('pages.email.emailTemplateCreatePage.panel.componentGallery1Col')
  },
  {
    key: 'gallery-2cols',
    type: 'gallery',
    columns: 2,
    label: t('pages.email.emailTemplateCreatePage.panel.componentGallery2Cols')
  },
  {
    key: 'gallery-3cols',
    type: 'gallery',
    columns: 3,
    label: t('pages.email.emailTemplateCreatePage.panel.componentGallery3Cols')
  }
])

const cloneComponent = (component: DraggableComponent): EmailBlock => {
  const id = `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

  switch (component.type) {
    case 'text':
      return {
        id,
        type: 'text',
        content: {
          type: 'doc',
          content: [{ type: 'paragraph' }]
        },
        align: 'left',
        color: '000000'
      }
    case 'image':
      return {
        id,
        type: 'image',
        src: '',
        alt: '',
        width: 100,
        align: 'center',
        link: '',
        mask: 'none'
      }
    case 'button':
      return {
        id,
        type: 'button',
        text: t('pages.email.emailTemplateCreatePage.panel.defaultButtonText'),
        url: '',
        backgroundColor: props.colorPresets[0]?.color || '1e293b',
        textColor: 'ffffff',
        align: 'center'
      }
    case 'spacer':
      return {
        id,
        type: 'spacer',
        height: 20
      }
    case 'separator':
      return {
        id,
        type: 'separator',
        color: 'e2e8f0',
        height: 16
      }
    case 'social':
      return {
        id,
        type: 'social',
        links: [
          { id: `${id}-fb`, network: 'facebook', url: '' },
          { id: `${id}-ig`, network: 'instagram', url: '' },
          { id: `${id}-tt`, network: 'tiktok', url: '' }
        ],
        align: 'center',
        iconSet: 'brand',
        iconSize: 40,
        iconStyle: 'filled'
      }
    case 'gallery': {
      const emptyImage = () => ({ type: 'image' as const, src: '', alt: '', link: '' })
      const columns: GalleryColumns = component.columns ?? 1
      return {
        id,
        type: 'gallery',
        layout: 'grid',
        columns,
        gap: 16,
        items: Array.from({ length: columns }, emptyImage)
      }
    }
    default:
      throw new Error(`Unknown block type: ${component.type}`)
  }
}
</script>

<template>
  <div class="flex w-90 flex-shrink-0 flex-col border-r border-border bg-white">
    <div class="flex h-[77px] items-center justify-between border-b border-border px-4">
      <div class="flex items-center gap-2">
        <span class="text-base text-foreground">E-mail</span>
      </div>
    </div>

    <div class="relative flex-1 overflow-hidden">
      <!-- Settings view (block-specific). Always mounted so Teleport targets stay alive. -->
      <div
        class="absolute inset-0 flex flex-col bg-white transition-transform duration-200 ease-out"
        :class="panelView === 'settings' ? 'translate-x-0' : 'pointer-events-none translate-x-6 opacity-0'"
      >
        <div class="flex items-center gap-2 border-b border-border px-4 py-3">
          <Button type="button" variant="ghost" size="icon-sm" class="shrink-0" @click="emit('deselect')">
            <ArrowLeft />
          </Button>
          <div class="flex min-w-0 items-center gap-2">
            <BaseIcon
              v-if="selectedBlockIcon"
              :name="selectedBlockIcon"
              class="size-4 shrink-0 text-muted-foreground"
            />
            <span class="truncate text-sm font-medium text-foreground">{{ selectedBlockLabel }}</span>
          </div>
        </div>
        <div id="email-settings-panel" class="flex-1 overflow-y-auto"></div>
      </div>

      <Transition name="panel-slide-out">
        <!-- Add view (default) -->
        <div v-if="panelView === 'add'" key="add" class="absolute inset-0 space-y-6 overflow-y-auto bg-white px-5 py-4">
          <BaseTip :text="t('pages.email.emailTemplateCreatePage.selectBlock')" severity="info" size="sm">
            <template #icon>
              <MousePointer2 />
            </template>
          </BaseTip>

          <div>
            <EmailEditorBlockHeader
              icon="IconoirPalette"
              :title="t('pages.email.emailTemplateCreatePage.panel.colors')"
              class="mb-3"
            />
            <div class="flex flex-col gap-1.5">
              <label class="text-xs text-muted-foreground">{{
                t('pages.email.emailTemplateCreatePage.panel.emailBackground')
              }}</label>
              <ColorPickerWithPresets v-model="emailStyles.contentBackgroundColor" :presets="colorPresets" />
            </div>
            <div class="mt-3 flex flex-col gap-1.5">
              <label class="text-xs text-muted-foreground">{{
                t('pages.email.emailTemplateCreatePage.panel.canvasBackground')
              }}</label>
              <ColorPickerWithPresets v-model="emailStyles.backgroundColor" :presets="colorPresets" />
            </div>
          </div>

          <div>
            <EmailEditorBlockHeader
              icon="IconoirText"
              :title="t('pages.email.emailTemplateCreatePage.panel.font')"
              class="mb-3"
            />
            <ComboboxSelect
              v-model="emailStyles.fontFamily"
              :options="fontComboboxOptions"
              :placeholder="t('pages.email.emailTemplateCreatePage.panel.font')"
              disable-portal
            >
              <template #option="{ option }">
                <div class="flex flex-col gap-0.5">
                  <span class="text-sm" :style="{ fontFamily: option.value }">{{ option.label }}</span>
                  <span class="text-xs text-muted-foreground">
                    {{ fontOptions.find((f) => f.value === option.value)?.description }}
                  </span>
                </div>
              </template>
            </ComboboxSelect>
          </div>

          <div>
            <EmailEditorBlockHeader
              icon="IconoirLayoutLeft"
              :title="t('pages.email.emailTemplateCreatePage.panel.baseComponents')"
              class="mb-3"
            />
            <draggable
              :list="baseComponents"
              :group="{ name: 'email-blocks', pull: 'clone', put: false }"
              :clone="cloneComponent"
              :sort="false"
              class="grid grid-cols-3 gap-2"
            >
              <div
                v-for="component in baseComponents"
                :key="component.key"
                class="flex aspect-square cursor-grab flex-col items-center justify-center gap-2.5 rounded-lg border border-border bg-muted p-2 transition-all hover:border-border hover:bg-accent active:cursor-grabbing"
              >
                <component
                  :is="component.iconComponent"
                  v-if="component.iconComponent"
                  class="h-5 w-5 text-muted-foreground"
                />
                <BaseIcon v-else-if="component.icon" :name="component.icon" class="h-5 w-5 text-muted-foreground" />
                <span class="text-center text-xs text-foreground-secondary">{{ component.label }}</span>
              </div>
            </draggable>
          </div>

          <div>
            <EmailEditorBlockHeader :title="t('pages.email.emailTemplateCreatePage.panel.layoutBlocks')" class="mb-3">
              <template #icon>
                <Columns3 class="size-4 text-muted-foreground" />
              </template>
            </EmailEditorBlockHeader>
            <draggable
              :list="layoutComponents"
              :group="{ name: 'email-blocks', pull: 'clone', put: false }"
              :clone="cloneComponent"
              :sort="false"
              class="grid grid-cols-3 gap-2"
            >
              <div
                v-for="component in layoutComponents"
                :key="component.key"
                class="flex aspect-square cursor-grab flex-col items-center justify-center gap-2.5 rounded-lg border border-border bg-muted p-2 transition-all hover:border-border hover:bg-accent active:cursor-grabbing"
              >
                <GalleryPresetIcon
                  v-if="component.columns"
                  :columns="component.columns"
                  class="h-5 w-5 text-muted-foreground"
                />
                <span class="text-xs text-foreground-secondary">{{ component.label }}</span>
              </div>
            </draggable>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.panel-slide-out-enter-active,
.panel-slide-out-leave-active {
  transition:
    transform 200ms cubic-bezier(0.32, 0.72, 0, 1),
    opacity 180ms ease-out;
}

.panel-slide-out-enter-from {
  transform: translateX(-24px);
  opacity: 0;
}
.panel-slide-out-leave-to {
  transform: translateX(-24px);
  opacity: 0;
}
</style>
